import { action, computed, observable, makeAutoObservable } from 'mobx';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { apiCommon } from 'src/api/ApiCommon';
import { CasinoMobileVersionModelType } from 'src/api/config/casinoMobileVersion/getCasinoMobileVersion';
import { ParamsType } from 'src/api_openapi/generated/openapi_website_cms_createCasinoMobileVersion';
import { FormModel, Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';

type CasinoMobileVersionBodyType = ParamsType['body'];

export interface CasinoMobileVersionType {
    app_version: string;
    is_casino_active: boolean;
}

const validateAppVersion =
    () =>
    (value: string): Result<string> => {
        const firstLetter = Number(value[0]);
        const lastLetter = Number(value[value.length - 1]);

        if (isNaN(firstLetter) || isNaN(lastLetter)) {
            return Result.createError('First and last letter have to be a number - ex. - 1.2.3');
        }

        const valueWithoutDots = Number(value.replaceAll('.', ''));

        if (isNaN(valueWithoutDots)) {
            return Result.createError('Version can contain only dots and numbers - ex. - 1.2.3');
        }

        return Result.createOk(value);
    };

export class CasinoMobileVersionState {
    @observable private readonly casinoMobileVersionListResource: Resource<Array<CasinoMobileVersionModelType>>;
    @observable public errorMessageForList: string | null = null;

    public readonly appVersion: FormInputState<string, string>;
    public readonly isCasinoActive: FormInputState<boolean, boolean>;

    private readonly form: FormModel<CasinoMobileVersionType>;

    constructor() {
        this.casinoMobileVersionListResource = new Resource(async (): Promise<Array<CasinoMobileVersionModelType>> => {
            return await apiCommon.getCasinoMobileVersion.run({});
        });

        this.appVersion = FormInputState.new('').map(validateNotEmpty('Cannot be not empty')).map(validateAppVersion());
        this.isCasinoActive = FormInputState.new<boolean>(false);
        this.form = FormModel.group({
            app_version: this.appVersion,
            is_casino_active: this.isCasinoActive,
        });
        makeAutoObservable(this);
    }

    @action private handleCreateApiCall = async (
        casinoMobileVersion: CasinoMobileVersionBodyType
    ): Promise<number | null> => {
        try {
            return await apiCommon.postCreateCasinoMobileVersion.run({ data: casinoMobileVersion });
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    @action public handleCreate = async (): Promise<void> => {
        this.form.setAsVisited();
        const result = this.form.result;
        const body = result.value;
        if (body.type === 'ok') {
            const submitResult = await this.handleCreateApiCall(body.data);

            if (submitResult !== null) {
                await this.casinoMobileVersionListResource.refresh();
                this.form.reset();
            } else {
                console.log('Error');
                this.handleError('Something went wrong.');
            }
        }
    };

    @computed public get casinoMobileVersionList(): Array<CasinoMobileVersionModelType> {
        const casinoMobileVersionListResource = this.casinoMobileVersionListResource.get();
        if (casinoMobileVersionListResource.type === 'ready') {
            return casinoMobileVersionListResource.value;
        }
        return [];
    }

    @computed public get casinoMobileVersionSortedList(): Array<CasinoMobileVersionModelType> {
        return this.casinoMobileVersionList.sort((a, b) => {
            const aValue = a.app_version;
            const bValue = b.app_version;

            if (aValue > bValue) {
                return 1;
            }
            if (aValue < bValue) {
                return -1;
            }
            return 0;
        });
    }

    @computed public get isLoading(): boolean {
        const casinoMobileVersionListResource = this.casinoMobileVersionListResource.get();
        return casinoMobileVersionListResource.type === 'loading';
    }

    @action public handleRemoveApiCall = async (app_version: string): Promise<void> => {
        const removeResult = await apiCommon.deleteCasinoMobileVersion.run({ app_version });

        if (removeResult) {
            await this.casinoMobileVersionListResource.refresh();
        } else {
            console.log('Error');
        }
    };

    @action public setActiveFlag = (app_version: string): void => {
        const casinoMobileVersion =
            this.casinoMobileVersionList.find((elem) => elem.app_version === app_version) ?? null;
        if (casinoMobileVersion !== null) {
            const payload: CasinoMobileVersionModelType = {
                ...casinoMobileVersion,
                is_casino_active: !casinoMobileVersion.is_casino_active,
            };

            this.handleEditActiveFlags(payload);
        }
    };

    @action private handleEditApiCall = async (
        casinoMobileVersion: CasinoMobileVersionBodyType
    ): Promise<number | null> => {
        try {
            return await apiCommon.putUpdateCasinoMobileVersion.run({ data: casinoMobileVersion });
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    @action private handleEditActiveFlags = async (payload: CasinoMobileVersionModelType): Promise<void> => {
        const submitResult = await this.handleEditApiCall(payload);

        if (submitResult !== null) {
            await this.casinoMobileVersionListResource.refresh();
            this.form.reset();
        } else {
            console.log('Error');
        }
    };

    @action handleError = async (message: string): Promise<void> => {
        this.errorMessageForList = message;
        await this.wait(3000);
        this.errorMessageForList = null;
    };

    wait = async (time: number): Promise<void> => {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve();
            }, time);
        });
    };
}
